<script setup lang="ts">
  defineOptions({ name: 'EventDetailExhibitorDrawer' })

  import { computed, onMounted, onUnmounted, ref, type PropType } from 'vue'

  import { useI18n } from 'vue-i18n'

  import { Globe01Icon, XCloseIcon } from '@gohighlevel/ghl-icons/24/outline'

  import { createElementId } from '@common/core-layer/utils'

  import CPAvatar from '@ui/components/CPAvatar.vue'
  import CPButton from '@ui/components/CPButton.vue'
  import CPDrawer from '@ui/components/CPDrawer.vue'
  import CPText from '@ui/components/CPText.vue'

  import { exhibitorWebsiteLabel } from '../utils/map-event-exhibitors'

  import type { EventDetailExhibitor, EventDetailExhibitorRep } from '../types/event-detail'
  import type { EventsHubResourceState } from '../types/events-hub'

  const props = defineProps({
    show: {
      type: Boolean,
      default: false,
    },
    exhibitor: {
      type: Object as PropType<EventDetailExhibitor | null>,
      default: null,
    },
    state: {
      type: String as PropType<EventsHubResourceState>,
      default: 'empty',
    },
    representatives: {
      type: Array as PropType<readonly EventDetailExhibitorRep[]>,
      default: () => [],
    },
    idPrefix: {
      type: String,
      default: 'event-detail',
    },
  })

  const emit = defineEmits<{
    'update:show': [value: boolean]
  }>()

  const { t } = useI18n()

  const COMPACT_VIEWPORT_QUERY = '(max-width: 767px)'

  const compactQuery =
    typeof window !== 'undefined' && typeof window.matchMedia === 'function' ? window.matchMedia(COMPACT_VIEWPORT_QUERY) : null
  const isCompact = ref(compactQuery?.matches === true)
  const aboutCopy = computed(() => props.exhibitor?.description?.trim() ?? '')
  const showAbout = computed(() => props.exhibitor?.showAboutAndLinks !== false && Boolean(aboutCopy.value))
  const websiteUrl = computed(() => props.exhibitor?.websiteUrl?.trim() ?? '')
  const websiteLabel = computed(() => (websiteUrl.value ? exhibitorWebsiteLabel(websiteUrl.value) : ''))
  const showWebsite = computed(() => props.exhibitor?.showAboutAndLinks !== false && Boolean(websiteUrl.value))
  const showTeam = computed(() => props.exhibitor?.showTeamAtEvent !== false)
  const isLoading = computed(() => props.state === 'loading')
  const isError = computed(() => props.state === 'error')
  const repCountLabel = computed(() => {
    const count = props.representatives.length
    return count === 1
      ? t('clientportal.events.detail.exhibitorsRepCountOne')
      : t('clientportal.events.detail.exhibitorsRepCountMany', { count })
  })
  const drawerPlacement = computed(() => (isCompact.value ? 'bottom' : 'right'))
  const drawerWidth = computed(() => (isCompact.value ? '100%' : 480))
  const drawerHeight = computed(() => (isCompact.value ? '92%' : undefined))

  const closeDrawer = () => {
    emit('update:show', false)
  }

  const syncCompactViewport = () => {
    isCompact.value = compactQuery?.matches === true
  }

  onMounted(() => {
    compactQuery?.addEventListener('change', syncCompactViewport)
  })

  onUnmounted(() => {
    compactQuery?.removeEventListener('change', syncCompactViewport)
  })
</script>

<template>
  <CPDrawer
    v-if="exhibitor"
    :id="createElementId(idPrefix, 'exhibitor-drawer', exhibitor.id)"
    :key="drawerPlacement"
    :show="show"
    :placement="drawerPlacement"
    :width="drawerWidth"
    :height="drawerHeight"
    :closable="false"
    :show-header="isCompact"
    :mask-closable="true"
    :z-index="10001"
    header-style="border-bottom: none"
    body-class="p-0"
    body-content-class="!p-0"
    @update:show="emit('update:show', $event)"
  >
    <template v-if="isCompact" #header>
      <div class="flex items-center justify-center py-2">
        <div class="h-[0.1875rem] w-[3.125rem] rounded-full bg-dark-gray" />
      </div>
    </template>

    <div class="flex min-h-0 flex-1 flex-col overflow-y-auto">
      <div class="relative flex flex-col items-center gap-2.5 border-b border-[var(--cp-border-500)] bg-[var(--cp-primary-25)] px-5 py-6">
        <div class="flex w-full items-start justify-between">
          <CPText size="xs" weight="bold" class="uppercase tracking-wide text-500">
            {{ t('clientportal.events.detail.exhibitorsEyebrow') }}
          </CPText>
          <CPButton
            :id="createElementId(idPrefix, 'exhibitor-drawer-close', exhibitor.id)"
            type="text"
            color="secondary"
            size="xs"
            :aria-label="t('common.buttons.close')"
            @click="closeDrawer"
          >
            <XCloseIcon class="h-5 w-5" aria-hidden="true" />
          </CPButton>
        </div>
        <CPAvatar :title="exhibitor.name" :src="exhibitor.logoUrl" size="2xl" source="client-portal" :use-raw-url="true" />
        <CPText size="2xl" weight="bold" html-tag="h2" class="text-center text-body">
          {{ exhibitor.name }}
        </CPText>
        <CPText v-if="exhibitor.booth" size="md" weight="normal" class="text-center text-500">
          {{ exhibitor.booth }}
        </CPText>
      </div>

      <div class="flex flex-col gap-2.5 px-5 py-[1.125rem]">
        <template v-if="showAbout">
          <CPText size="lg" weight="bold" html-tag="h3" class="text-body">
            {{ t('clientportal.events.detail.exhibitorsAbout') }}
          </CPText>
          <CPText :id="createElementId(idPrefix, 'exhibitor-drawer', 'about')" size="md" weight="normal" class="text-600">
            {{ aboutCopy }}
          </CPText>
        </template>

        <a
          v-if="showWebsite"
          :id="createElementId(idPrefix, 'exhibitor-drawer-site', exhibitor.id)"
          :href="websiteUrl"
          target="_blank"
          rel="noopener noreferrer"
          class="inline-flex h-9 w-fit items-center justify-center gap-2 rounded-lg border border-solid border-[var(--cp-border-500)] px-3 text-[0.8125rem] font-semibold text-700 shadow-sm"
          :aria-label="t('clientportal.events.detail.exhibitorsWebsiteAria', { label: websiteLabel })"
        >
          <Globe01Icon class="h-4 w-4 shrink-0" aria-hidden="true" />
          <span>{{ websiteLabel }}</span>
        </a>

        <template v-if="showTeam">
          <div class="mt-2 flex items-center gap-2">
            <CPText size="lg" weight="bold" html-tag="h3" class="text-body">
              {{ t('clientportal.events.detail.exhibitorsWhoYoullMeet') }}
            </CPText>
            <CPText v-if="!isLoading && !isError" size="sm" weight="normal" class="text-500">
              {{ repCountLabel }}
            </CPText>
          </div>
          <CPText v-if="isLoading" size="md" weight="normal" class="py-4 text-center text-500">
            {{ t('clientportal.events.detail.exhibitorsMembersLoading') }}
          </CPText>
          <CPText v-else-if="isError" size="md" weight="normal" class="py-4 text-center text-500">
            {{ t('clientportal.events.detail.exhibitorsMembersError') }}
          </CPText>
          <CPText v-else-if="representatives.length === 0" size="md" weight="normal" class="py-4 text-center text-500">
            {{ t('clientportal.events.detail.exhibitorsMembersEmpty') }}
          </CPText>
          <div v-else class="flex flex-col gap-2">
            <div
              v-for="rep in representatives"
              :id="createElementId(idPrefix, 'exhibitor-drawer-rep', `${exhibitor.id}-${rep.id}`)"
              :key="rep.id"
              class="flex items-center gap-3 rounded-[10px] border border-solid border-[var(--cp-border-500)] px-3.5 py-3.5"
            >
              <CPAvatar :title="rep.name" size="md" source="client-portal" class="shrink-0" />
              <div class="min-w-0 flex-1">
                <CPText size="md" weight="semibold" html-tag="div" class="truncate text-body">
                  {{ rep.name }}
                </CPText>
                <CPText v-if="rep.role" size="sm" weight="normal" html-tag="div" class="mt-0.5 truncate text-500">
                  {{ rep.role }}
                </CPText>
              </div>
            </div>
          </div>
        </template>
      </div>
    </div>
  </CPDrawer>
</template>
